import { useState, useRef } from 'react';
import axios from 'axios';
import { API, useAuth } from '../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Upload, FileSpreadsheet, CheckCircle, AlertCircle, Download, ChefHat, Package, UtensilsCrossed } from 'lucide-react';
import Papa from 'papaparse';

const ImportPage = () => {
  const { isAdmin } = useAuth();
  const fileInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState('ingredients');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const isExcel = selectedFile.name.match(/\.xlsx?$/i);
    const isCsv = selectedFile.name.match(/\.csv$/i);

    if (activeTab === 'ingredients' && !isCsv) {
      toast.error('Please select a CSV file for ingredients import');
      return;
    }

    if ((activeTab === 'sub-recipes' || activeTab === 'final-recipes') && !isExcel) {
      toast.error('Please select an Excel file (.xlsx) for this import');
      return;
    }

    setFile(selectedFile);
    setImportResult(null);

    if (activeTab === 'ingredients' && isCsv) {
      Papa.parse(selectedFile, {
        header: true,
        preview: 10,
        complete: (results) => {
          setPreview({
            type: 'ingredients',
            headers: results.meta.fields || [],
            rows: results.data,
            totalRows: results.data.length
          });
        },
        error: (error) => {
          toast.error('Failed to parse CSV file');
          console.error(error);
        }
      });
    } else if (activeTab === 'sub-recipes' && isExcel) {
      await previewSubRecipes(selectedFile);
    } else if (activeTab === 'final-recipes' && isExcel) {
      await previewFinalRecipes(selectedFile);
    }
  };

  const previewSubRecipes = async (selectedFile) => {
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      const response = await axios.post(`${API}/import/sub-recipes/preview`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setPreview({
        type: 'sub-recipes',
        ...response.data
      });
    } catch (error) {
      toast.error('Failed to preview sub-recipes file');
      console.error(error);
    }
  };

  const previewFinalRecipes = async (selectedFile) => {
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      const response = await axios.post(`${API}/import/final-recipes/preview`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setPreview({
        type: 'final-recipes',
        ...response.data
      });
    } catch (error) {
      toast.error('Failed to preview final recipes file');
      console.error(error);
    }
  };

  const handleImport = async () => {
    if (!file) return;

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      let endpoint;
      if (activeTab === 'ingredients') {
        endpoint = `${API}/import/ingredients`;
      } else if (activeTab === 'sub-recipes') {
        endpoint = `${API}/import/sub-recipes`;
      } else {
        endpoint = `${API}/import/final-recipes`;
      }

      const response = await axios.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setImportResult(response.data);
      
      if (activeTab === 'ingredients') {
        toast.success(`Successfully imported ${response.data.imported_count} ingredients`);
      } else if (activeTab === 'sub-recipes') {
        toast.success(`Imported ${response.data.imported_count} new, updated ${response.data.updated_count} existing sub-recipes`);
      } else {
        toast.success(`Imported ${response.data.imported_count} new, updated ${response.data.updated_count} existing final recipes`);
      }
    } catch (error) {
      toast.error('Import failed');
      setImportResult({ error: error.response?.data?.detail || error.message });
    } finally {
      setImporting(false);
    }
  };

  const handleSeedDemo = async () => {
    try {
      await axios.post(`${API}/seed`);
      toast.success('Demo data seeded successfully');
    } catch (error) {
      toast.error('Failed to seed demo data');
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      const fakeEvent = { target: { files: [droppedFile] } };
      handleFileSelect(fakeEvent);
    }
  };

  const resetImport = () => {
    setFile(null);
    setPreview(null);
    setImportResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleTabChange = (value) => {
    setActiveTab(value);
    resetImport();
  };

  if (!isAdmin) {
    return (
      <div className="max-w-[1600px] mx-auto p-4 sm:p-6 lg:p-8">
        <div className="bg-white border border-zinc-200 p-12 text-center">
          <AlertCircle className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
          <h2 className="text-xl font-medium mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
            Admin Access Required
          </h2>
          <p className="text-zinc-500">
            Only administrators can import data. Please switch to Admin role to access this feature.
          </p>
        </div>
      </div>
    );
  }

  const getFileAccept = () => {
    if (activeTab === 'ingredients') return '.csv';
    return '.xlsx,.xls';
  };

  const getFileTypeLabel = () => {
    if (activeTab === 'ingredients') return 'Supports CSV files';
    return 'Supports Excel files (.xlsx)';
  };

  return (
    <div className="max-w-[1600px] mx-auto p-4 sm:p-6 lg:p-8" data-testid="import-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-medium text-zinc-950" style={{ fontFamily: 'var(--font-heading)' }}>
            Import Data
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Import ingredients, sub-recipes, and final recipes from files
          </p>
        </div>
        <Button
          onClick={handleSeedDemo}
          variant="outline"
          className="rounded-none border-zinc-300"
          data-testid="seed-demo-btn"
        >
          <Download className="w-4 h-4 mr-2" />
          Load Demo Data
        </Button>
      </div>

      {/* Import Type Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="mb-6">
        <TabsList className="bg-zinc-100 p-1 rounded-none">
          <TabsTrigger 
            value="ingredients" 
            className="rounded-none data-[state=active]:bg-white px-4"
            data-testid="tab-ingredients"
          >
            <Package className="w-4 h-4 mr-2" />
            Ingredients
          </TabsTrigger>
          <TabsTrigger 
            value="sub-recipes" 
            className="rounded-none data-[state=active]:bg-white px-4"
            data-testid="tab-sub-recipes"
          >
            <ChefHat className="w-4 h-4 mr-2" />
            Sub-Recipes
          </TabsTrigger>
          <TabsTrigger 
            value="final-recipes" 
            className="rounded-none data-[state=active]:bg-white px-4"
            data-testid="tab-final-recipes"
          >
            <UtensilsCrossed className="w-4 h-4 mr-2" />
            Final Recipes
          </TabsTrigger>
        </TabsList>

        {/* Ingredients Tab */}
        <TabsContent value="ingredients" className="mt-6">
          <div className="bg-white border border-zinc-200 p-6 mb-6">
            <h3 className="text-sm font-medium mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
              Expected CSV Format
            </h3>
            <div className="overflow-x-auto">
              <table className="data-table text-xs">
                <thead>
                  <tr>
                    <th>Ingredient</th>
                    <th>Purchased Cost</th>
                    <th>Purchased Total Unit</th>
                    <th>Supplier</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Mild Grated Cheddar Cheese</td>
                    <td className="font-mono">29.06</td>
                    <td className="font-mono">5000</td>
                    <td>BidFood</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-xs text-zinc-500 mt-3">
              Duplicate ingredients will be updated with new values. Unit type defaults to grams (g).
            </p>
          </div>
        </TabsContent>

        {/* Sub-Recipes Tab */}
        <TabsContent value="sub-recipes" className="mt-6">
          <div className="bg-white border border-zinc-200 p-6 mb-6">
            <h3 className="text-sm font-medium mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
              Expected Excel Format
            </h3>
            <div className="text-sm text-zinc-600 space-y-2">
              <p>Your Excel file should have sub-recipes with: Recipe Name, Yield Amount, and Ingredients list.</p>
            </div>
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 text-blue-800 text-xs">
              <strong>Note:</strong> Ingredients will be matched to your existing database. Costs will be calculated using your current ingredient prices.
            </div>
          </div>
        </TabsContent>

        {/* Final Recipes Tab */}
        <TabsContent value="final-recipes" className="mt-6">
          <div className="bg-white border border-zinc-200 p-6 mb-6">
            <h3 className="text-sm font-medium mb-3" style={{ fontFamily: 'var(--font-heading)' }}>
              Final Recipes Import
            </h3>
            <div className="text-sm text-zinc-600 space-y-2">
              <p>Import final recipes from your Excel file. The system will:</p>
              <ul className="list-disc list-inside mt-2 space-y-1 text-xs">
                <li><strong>Chilled & Baked Goods:</strong> Import as single Standard size recipe</li>
                <li><strong>Heat to Eat & Weekly Specials:</strong> Create separate Small and Large recipes</li>
                <li><strong>Snack Pots:</strong> Import as Final Recipes and remove from Sub-Recipes if exists</li>
              </ul>
            </div>
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 text-amber-800 text-xs">
              <strong>Important:</strong> Components will be matched to your existing Ingredients and Sub-Recipes. Make sure to import those first!
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Upload Area */}
      {!file ? (
        <div
          className="border-2 border-dashed border-zinc-300 bg-white p-12 text-center cursor-pointer hover:border-zinc-400 hover:bg-zinc-50 transition-colors"
          onClick={() => fileInputRef.current?.click()}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          data-testid="file-drop-zone"
        >
          <Upload className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
            Drop your file here
          </h3>
          <p className="text-sm text-zinc-500 mb-4">
            or click to browse
          </p>
          <p className="text-xs text-zinc-400">
            {getFileTypeLabel()}
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept={getFileAccept()}
            onChange={handleFileSelect}
            className="hidden"
            data-testid="file-input"
          />
        </div>
      ) : (
        <div className="bg-white border border-zinc-200">
          {/* File Info */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200">
            <div className="flex items-center gap-3">
              <FileSpreadsheet className="w-8 h-8 text-zinc-400" />
              <div>
                <div className="font-medium">{file.name}</div>
                <div className="text-xs text-zinc-500">
                  {(file.size / 1024).toFixed(1)} KB
                </div>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={resetImport}
              className="rounded-none border-zinc-300"
              data-testid="reset-import-btn"
            >
              Choose Different File
            </Button>
          </div>

          {/* Preview - Ingredients */}
          {preview && preview.type === 'ingredients' && !importResult && (
            <div className="p-6">
              <h3 className="text-[11px] uppercase tracking-[0.15em] font-bold text-zinc-500 mb-3">
                Preview (First {preview.rows?.length || 0} rows)
              </h3>
              <div className="overflow-x-auto border border-zinc-200">
                <table className="data-table text-xs">
                  <thead>
                    <tr>
                      {preview.headers?.map((header) => (
                        <th key={header}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows?.map((row, rowIdx) => (
                      <tr key={`row-${rowIdx}`}>
                        {preview.headers?.map((header) => (
                          <td key={header} className={typeof row[header] === 'number' ? 'font-mono' : ''}>
                            {row[header] || '-'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-6 flex justify-end">
                <Button
                  onClick={handleImport}
                  disabled={importing}
                  className="rounded-none bg-zinc-950 text-white hover:bg-zinc-800 px-8"
                  data-testid="confirm-import-btn"
                >
                  {importing ? 'Importing...' : 'Import Ingredients'}
                </Button>
              </div>
            </div>
          )}

          {/* Preview - Sub-Recipes */}
          {preview && preview.type === 'sub-recipes' && !importResult && (
            <div className="p-6">
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-zinc-50 p-4 border border-zinc-200">
                  <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-zinc-500">
                    Sub-Recipes Found
                  </div>
                  <div className="text-2xl font-mono font-medium mt-1">{preview.total_recipes}</div>
                </div>
                <div className="bg-zinc-50 p-4 border border-zinc-200">
                  <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-zinc-500">
                    Ingredients in DB
                  </div>
                  <div className="text-2xl font-mono font-medium mt-1">{preview.total_ingredients_in_db}</div>
                </div>
                <div className="bg-zinc-50 p-4 border border-zinc-200">
                  <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-zinc-500">
                    Existing Sub-Recipes
                  </div>
                  <div className="text-2xl font-mono font-medium mt-1">{preview.total_sub_recipes_in_db}</div>
                </div>
              </div>

              <h3 className="text-[11px] uppercase tracking-[0.15em] font-bold text-zinc-500 mb-3">
                Preview (First {preview.preview_recipes?.length || 0} of {preview.total_recipes})
              </h3>
              
              <div className="border border-zinc-200 divide-y divide-zinc-200 max-h-[400px] overflow-y-auto">
                {preview.preview_recipes?.map((recipe) => (
                  <div key={recipe.name} className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-medium">{recipe.name}</div>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="text-zinc-500">
                          Yield: <span className="font-mono">{recipe.yield_amount}g</span>
                        </span>
                        <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 font-medium">
                          {recipe.matched_count} matched
                        </span>
                        {recipe.unmatched_count > 0 && (
                          <span className="px-2 py-0.5 bg-amber-50 text-amber-700 font-medium">
                            {recipe.unmatched_count} unmatched
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 flex justify-end gap-4">
                <Button
                  onClick={handleImport}
                  disabled={importing}
                  className="rounded-none bg-zinc-950 text-white hover:bg-zinc-800 px-8"
                  data-testid="confirm-import-btn"
                >
                  {importing ? 'Importing...' : `Import ${preview.total_recipes} Sub-Recipes`}
                </Button>
              </div>
            </div>
          )}

          {/* Preview - Final Recipes */}
          {preview && preview.type === 'final-recipes' && !importResult && (
            <div className="p-6">
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-zinc-50 p-4 border border-zinc-200">
                  <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-zinc-500">
                    Final Recipes Found
                  </div>
                  <div className="text-2xl font-mono font-medium mt-1">{preview.total_recipes}</div>
                </div>
                <div className="bg-zinc-50 p-4 border border-zinc-200">
                  <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-zinc-500">
                    Ingredients in DB
                  </div>
                  <div className="text-2xl font-mono font-medium mt-1">{preview.total_ingredients_in_db}</div>
                </div>
                <div className="bg-zinc-50 p-4 border border-zinc-200">
                  <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-zinc-500">
                    Sub-Recipes in DB
                  </div>
                  <div className="text-2xl font-mono font-medium mt-1">{preview.total_sub_recipes_in_db}</div>
                </div>
              </div>

              <h3 className="text-[11px] uppercase tracking-[0.15em] font-bold text-zinc-500 mb-3">
                Categories Found
              </h3>
              
              <div className="border border-zinc-200 divide-y divide-zinc-200 max-h-[400px] overflow-y-auto">
                {preview.categories && Object.entries(preview.categories).map(([category, data]) => (
                  <div key={category} className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="font-medium text-lg">{category}</div>
                      <span className="px-3 py-1 bg-zinc-100 text-zinc-700 font-mono font-medium">
                        {data.count} recipes
                      </span>
                    </div>
                    <div className="space-y-2">
                      {data.recipes?.map((recipe) => (
                        <div key={recipe.name} className="flex items-center justify-between text-sm bg-zinc-50 p-2">
                          <div>
                            <span className="font-medium">{recipe.name}</span>
                            {recipe.size !== 'Standard' && (
                              <span className="ml-2 px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium">
                                {recipe.size}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-xs">
                            <span className="text-zinc-500">{recipe.ingredient_count} items</span>
                            <span className="px-1.5 py-0.5 bg-emerald-50 text-emerald-700">
                              {recipe.matched} matched
                            </span>
                            {recipe.unmatched > 0 && (
                              <span className="px-1.5 py-0.5 bg-amber-50 text-amber-700">
                                {recipe.unmatched} unmatched
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                      {data.count > 5 && (
                        <div className="text-xs text-zinc-500 pl-2">
                          ... and {data.count - 5} more recipes
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 flex justify-end gap-4">
                <Button
                  onClick={handleImport}
                  disabled={importing}
                  className="rounded-none bg-zinc-950 text-white hover:bg-zinc-800 px-8"
                  data-testid="confirm-import-btn"
                >
                  {importing ? 'Importing...' : `Import ${preview.total_recipes} Final Recipes`}
                </Button>
              </div>
            </div>
          )}

          {/* Import Result */}
          {importResult && (
            <div className="p-6">
              {importResult.error ? (
                <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 text-red-700">
                  <AlertCircle className="w-5 h-5 mt-0.5" />
                  <div>
                    <div className="font-medium">Import Failed</div>
                    <div className="text-sm">{importResult.error}</div>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex items-start gap-3 p-4 bg-emerald-50 border border-emerald-200 text-emerald-700 mb-4">
                    <CheckCircle className="w-5 h-5 mt-0.5" />
                    <div>
                      <div className="font-medium">Import Successful</div>
                      <div className="text-sm">
                        {activeTab === 'ingredients' && (
                          <>{importResult.imported_count} ingredients imported</>
                        )}
                        {activeTab === 'sub-recipes' && (
                          <>{importResult.imported_count} new sub-recipes imported, {importResult.updated_count} updated</>
                        )}
                        {activeTab === 'final-recipes' && (
                          <>
                            {importResult.imported_count} new recipes imported, {importResult.updated_count} updated
                            {importResult.deleted_sub_recipes > 0 && (
                              <>, {importResult.deleted_sub_recipes} moved from Sub-Recipes</>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {importResult.errors?.length > 0 && (
                    <div className="p-4 bg-yellow-50 border border-yellow-200 text-yellow-700 mb-4">
                      <div className="font-medium mb-2">Warnings ({importResult.errors.length})</div>
                      <ul className="text-sm list-disc list-inside">
                        {importResult.errors.map((err, idx) => (
                          <li key={`err-${idx}`}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="flex justify-end gap-4">
                    <Button
                      variant="outline"
                      onClick={resetImport}
                      className="rounded-none border-zinc-300"
                    >
                      Import Another File
                    </Button>
                    <Button
                      onClick={() => {
                        if (activeTab === 'ingredients') window.location.href = '/ingredients';
                        else if (activeTab === 'sub-recipes') window.location.href = '/sub-recipes';
                        else window.location.href = '/recipes';
                      }}
                      className="rounded-none bg-zinc-950 text-white hover:bg-zinc-800"
                    >
                      View {activeTab === 'ingredients' ? 'Ingredients' : activeTab === 'sub-recipes' ? 'Sub-Recipes' : 'Recipes'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ImportPage;
